import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import Breadcrumb from "../../../Common/CommonBreadcrumb/Breadcrumb";
import axiosInstance from "../../../Services/Interceptor";
import { getFiscalYearData } from "../../../Redux/Actions/Apamanagement/FiscalYear/FiscalYearAction";
import WorkplaceMasterDataAddUpdate from "./WorkplaceMasterDataAddUpdate";
import { convertNumber } from "../../../shade/convertNumber";

const WorkplaceMasterData = ({ permission }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = localStorage.getItem("i18nextLng");
  const [workplaceData, setWorkplaceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const fiscalYear = useSelector((state) => state?.fiscalYear?.fiscalYear);
  const [fiscalYearId, setFiscalYearId] = useState(0);

  const handleEdit = (item) => {
    setEditData(item);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(
        `/apa-config/api/v1/strategic-objective-fy-mappings/${id}`
      );
      fetchWorkplaceData();
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  const fetchWorkplaceData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/apa-config/api/v1/strategic-objective-fy-mappings/all?fiscalYearId=${fiscalYearId}`
      );

      const sectionData =
        res?.data?.data?.map((item) => ({
          ...item,
          id: item.id,
        })) || [];

      setWorkplaceData(sectionData);
    } catch (error) {
      console.error("Failed to fetch workplace data", error);
    } finally {
      setLoading(false);
    }
  }, [fiscalYearId]);

  useEffect(() => {
    fetchWorkplaceData();
  }, [fetchWorkplaceData]);

  useEffect(() => {
    dispatch(getFiscalYearData(1));
  }, [dispatch]);

  const renderTable = () => {
    if (loading) {
      return (
        <div className="text-center p-5">
          <span className="spinner-border text-primary" />
        </div>
      );
    }

    if (!workplaceData.length) {
      return (
        <div className="text-center mt-5 border p-5">
          <p className="text-size-20 py-2" style={{ color: "#3C21F7" }}>
            {i18n.language === "en"
              ? "Please Add Workplace"
              : "কর্মসম্পাদনক্ষেত্র যোগ করুন"}
          </p>
        </div>
      );
    }

    return (
      <Table bordered responsive>
        <thead>
          <tr className="text-center">
            <th>{i18n.language === "en" ? "Fiscal Year" : "অর্থবছর"}</th>
            <th className="text-start">
              {i18n.language === "en"
                ? "Workplace Name"
                : "কর্মসম্পাদনক্ষেত্র নাম "}
            </th>
            <th>
              {i18n.language === "en" ? "Minimum Limit" : "সর্বনিম্ন সীমা"}
            </th>
            <th>
              {i18n.language === "en" ? "Maximum Limit" : "সর্বোচ্চ সীমা"}
            </th>
            <th>{i18n.language === "en" ? "Action" : "অ্যাকশন"}</th>
          </tr>
        </thead>
        <tbody>
          {workplaceData.map((item) => (
            <tr key={item.id}>
              <td className="text-center">
                {i18n.language === "en" ? item.fiscalYearEn : item.fiscalYearBn}
              </td>
              <td>{i18n.language === "en" ? item.nameEn : item.nameBn}</td>
              <td className="text-center">
                {convertNumber(item.minValue, currentLanguage)}
              </td>
              <td className="text-center">
                {" "}
                {convertNumber(item.maxValue, currentLanguage)}
              </td>
              <td className="text-center">
                <Button
                  size="sm"
                  variant="warning"
                  className="me-1"
                  onClick={() => handleEdit(item)}
                >
                  <i className="fa fa-edit" />
                </Button>

                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleDelete(item.id)}
                >
                  <i className="fa fa-trash" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };


  return (
    <div className="main-container container">
      <Breadcrumb
        toolkit={false}
        currentMenu={false}
        mainMenu={
          i18n.language === "en"
            ? permission?.menuTitleEn
            : permission?.menuTitleBn
        }
        subMenu={
          i18n.language === "en"
            ? permission?.subMenuTitleEn
            : permission?.subMenuTitleBn
        }
        subSubMenu={
          i18n.language === "en"
            ? permission?.subSubMenuTitleEn
            : permission?.subSubMenuTitleBn
        }
      />

      <Card>
        <Card.Body>
          <div className="text-end">
            <Button
              size="sm"
              className="btn-light-success p-2 fw-bold"
              onClick={() => {
                setOpenModal(true);
                setEditData(null);
              }}
            >
              {i18n.language === "en" ? "Add New" : "নতুন যোগ করুন"}{" "}
              <i className="fa fa-plus" />
            </Button>
          </div>
          <Row>
            {/* Fiscal Year */}
            <Col md={4} className="mb-3">
              <Form.Label>
                {t("Common.fiscalYear")} <span className="text-danger">*</span>
              </Form.Label>

              <div className="d-flex">
                <Form.Select
                  value={fiscalYearId}
                  onChange={(e) => setFiscalYearId(e.target.value)}
                >
                  <option value="">{t("CommonBtn.placeholder")}</option>
                  {fiscalYear?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {i18n.language === "en" ? item.titleEn : item.titleBn}
                    </option>
                  ))}
                </Form.Select>

                <Button
                  size="sm"
                  variant="none"
                  className="btn btn-primary btn-sm rounded-11 ms-2 text-nowrap"
                  onClick={() => setFiscalYearId(0)}
                >
                  {i18n.language === "en" ? "RESET" : "মুছে ফেলুন"}
                </Button>
              </div>
            </Col>
          </Row>

          {renderTable()}
        </Card.Body>
      </Card>

      {openModal && (
        <WorkplaceMasterDataAddUpdate
          openModal={openModal}
          setOpenModal={setOpenModal}
          fetchWorkplaceData={fetchWorkplaceData}
          editData={editData}
          setEditData={setEditData}
        />
      )}
    </div>
  );
};

export default WorkplaceMasterData;
